import { useEffect, useMemo, useRef, useState } from "react";
import { UA, WebSocketInterface } from "jssip";
import type { RTCSession } from "jssip/lib/RTCSession";
import { AgentConfig, AgentPresence, api } from "../lib/api";

type WsState = "offline" | "connecting" | "connected" | "available" | "busy" | "error";
type SipState = "offline" | "connecting" | "registered" | "error";

type CallOffer = {
  id?: string;
  callId?: string;
  canonicalCallId?: string;
  phone?: string;
  customerName?: string;
  serviceContext?: string;
  queueReason?: string;
  payload?: Record<string, unknown>;
  createdAt?: string;
  expiresAt?: string;
};

function stableDeviceId() {
  const key = "falcon-crm-web-headset-device-id";
  const existing = localStorage.getItem(key);
  if (existing) return existing;
  const id = `web-${crypto.randomUUID()}`;
  localStorage.setItem(key, id);
  return id;
}

function wsUrl(config?: AgentConfig | null) {
  if (config?.websocket_url) return config.websocket_url;
  const url = new URL("/crm-autodialer/api/ws", window.location.origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}

export function AgentHeadsetPage({ csrf }: { csrf: string }) {
  const [state, setState] = useState<WsState>("offline");
  const [message, setMessage] = useState("Ready");
  const [config, setConfig] = useState<AgentConfig | null>(null);
  const [presence, setPresence] = useState<AgentPresence | null>(null);
  const [offer, setOffer] = useState<CallOffer | null>(null);
  const [token, setToken] = useState("");
  const [connecting, setConnecting] = useState(false);
  const socketRef = useRef<WebSocket | null>(null);
  const heartbeatRef = useRef<number | null>(null);

  const [sipState, setSipState] = useState<SipState>("offline");
  const [sipCallActive, setSipCallActive] = useState(false);
  const [activeCallId, setActiveCallId] = useState("");
  const [muted, setMuted] = useState(false);

  const sipUaRef = useRef<UA | null>(null);
  const sipSessionRef = useRef<RTCSession | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const pendingAcceptedCallRef = useRef("");

  const routeId = presence?.deviceId || config?.desktop_route_id || config?.staff?.userId || "Not assigned";
  const endpoint = presence?.sipEndpoint || config?.sip?.endpoint || (config?.sip?.extension ? `PJSIP/${config.sip.extension}` : "");
  const contexts = useMemo(
    () => presence?.serviceContexts || config?.service_contexts || config?.service_permissions || [],
    [config, presence]
  );

  function stopHeartbeat() {
    if (heartbeatRef.current) window.clearInterval(heartbeatRef.current);
    heartbeatRef.current = null;
  }

  function stopSip() {
    const session = sipSessionRef.current;

    if (session && !session.isEnded()) {
      try {
        session.terminate();
      } catch {
        // Session may already be ending.
      }
    }

    sipSessionRef.current = null;

    const ua = sipUaRef.current;
    if (ua) {
      try {
        ua.stop();
      } catch {
        // UA may already be stopped.
      }
    }

    sipUaRef.current = null;

    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }

    pendingAcceptedCallRef.current = "";
    setSipCallActive(false);
    setActiveCallId("");
    setMuted(false);
    setSipState("offline");
  }

  async function startSip(cfg: AgentConfig) {
    const sip = cfg.sip;
    const extension = String(sip?.extension || sip?.username || "").trim();
    const username = String(sip?.username || extension).trim();
    const password = String(sip?.password || "");
    const domain = String(sip?.domain || window.location.hostname).trim();

    if (!extension || !username || !password || !domain) {
      throw new Error("Agent SIP configuration is incomplete");
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("Browser microphone access is not available");
    }

    setSipState("connecting");
    setMessage("Requesting microphone permission");

    const microphone = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: false
    });

    microphone.getTracks().forEach((track) => track.stop());

    const socket = new WebSocketInterface(
      `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}/falconphone/ws`
    );

    const ua = new UA({
      sockets: [socket],
      uri: `sip:${extension}@${domain}`,
      authorization_user: username,
      password,
      display_name: cfg.display_name || cfg.email || extension,
      register: true,
      session_timers: true
    });

    sipUaRef.current = ua;

    ua.on("connecting", () => {
      setSipState("connecting");
      setMessage("Connecting browser SIP");
    });

    const registered = new Promise<void>((resolve, reject) => {
      const timeout = window.setTimeout(() => reject(new Error("Browser SIP registration timed out")), 15000);
      ua.on("registered", () => {
        window.clearTimeout(timeout);
        setSipState("registered");
        setMessage("Browser SIP registered");
        resolve();
      });
      ua.on("registrationFailed", (event: unknown) => {
        window.clearTimeout(timeout);
        console.error("SIP registration failed", event);
        setSipState("error");
        setMessage("Browser SIP registration failed");
        reject(new Error("Browser SIP registration failed"));
      });
    });

    ua.on("disconnected", () => {
      if (sipUaRef.current === ua) {
        setSipState("error");
        setMessage("Browser SIP disconnected");
      }
    });

    ua.on("newRTCSession", (data: { session: RTCSession }) => {
      const session = data.session;

      // The Web CRM is an inbound agent headset. Do not accept an
      // unexpected SIP call unless this browser accepted a CRM offer.
      if (session.direction !== "incoming") return;

      if (!pendingAcceptedCallRef.current) {
        try {
          session.terminate({ status_code: 486, reason_phrase: "No accepted CRM offer" });
        } catch {
          session.terminate();
        }
        return;
      }

      sipSessionRef.current = session;
      setActiveCallId(pendingAcceptedCallRef.current);
      setSipCallActive(true);
      setMuted(false);
      setMessage("Connecting caller audio");

      const attachRemoteAudio = () => {
        const receiverTracks = session.connection
          .getReceivers()
          .map((receiver) => receiver.track)
          .filter((track): track is MediaStreamTrack => Boolean(track));

        if (receiverTracks.length && remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = new MediaStream(receiverTracks);
          void remoteAudioRef.current.play().catch(() => {
            setMessage("Call connected - tap the page if browser audio is blocked");
          });
        }
      };

      session.connection.addEventListener("track", attachRemoteAudio);

      session.on("confirmed", () => {
        attachRemoteAudio();
        setSipCallActive(true);
        setMessage("Call connected");
      });

      const clearSipCall = () => {
        if (sipSessionRef.current === session) {
          sipSessionRef.current = null;
        }

        if (remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = null;
        }

        pendingAcceptedCallRef.current = "";
        setSipCallActive(false);
        setActiveCallId("");
        setMuted(false);
        setMessage("Call ended");
      };

      session.on("ended", clearSipCall);
      session.on("failed", clearSipCall);

      try {
        session.answer({
          mediaConstraints: {
            audio: true,
            video: false
          }
        });
      } catch (err) {
        console.error("Could not answer browser SIP call", err);
        clearSipCall();
        setMessage("Could not connect browser audio");
      }
    });

    ua.start();
    await registered;
  }

  function closeSocket(nextState: WsState = "offline") {
    stopHeartbeat();
    socketRef.current?.close(1000, "user");
    socketRef.current = null;
    stopSip();
    setState(nextState);
  }

  async function goOnline() {
    if (connecting || state === "connected" || state === "available") return;
    setConnecting(true);
    setState("connecting");
    setMessage("Connecting headset");
    try {
      const deviceId = stableDeviceId();
      const session = await api.createDesktopSession({ deviceId, deviceName: "CRM Web Agent Headset" }, csrf);
      const cfg = session.config || (await api.getAgentConfig()).config;
      setConfig(cfg);
      setToken(session.token);

      // The browser SIP endpoint must be registered before the CRM
      // advertises this agent as AVAILABLE for an IVR handoff.
      await startSip(cfg);

      const ws = new WebSocket(wsUrl(cfg));
      socketRef.current = ws;
      ws.onopen = () => {
        ws.send(JSON.stringify({ type: "auth", token: session.token }));
      };
      ws.onmessage = (event) => {
        const msg = JSON.parse(String(event.data || "{}"));
        if (msg.type === "hello") {
          setState("available");
          setMessage("Agent headset online");
          if (msg.agent) setPresence(msg.agent);
          ws.send(JSON.stringify({ type: "agent.register", status: "AVAILABLE" }));
          heartbeatRef.current = window.setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: "agent.heartbeat", t: Date.now() }));
            }
          }, 15000);
          return;
        }
        if (msg.type === "agent.status" && msg.payload) {
          setPresence(msg.payload);
          const s = String(msg.payload.agentStatus || "").toUpperCase();
          setState(s === "BUSY" || s === "RINGING" ? "busy" : "available");
          return;
        }
        if (msg.type === "agent.call_offer" || msg.type === "call.offer") {
          const next = (msg.payload || msg.offer || msg) as CallOffer;
          setOffer(next);
          setState("busy");
          setMessage("Incoming IVR agent request");
          return;
        }
        if (msg.type === "agent.call.accepted") {
          setOffer(null);
          if (msg.agent) setPresence(msg.agent);
          setMessage("Call accepted. Use your registered SIP endpoint to answer.");
          return;
        }
        if (msg.type === "agent.call.rejected" || msg.type === "agent.call.busy") {
          setOffer(null);
          if (msg.agent) setPresence(msg.agent);
          setMessage("Call offer cleared");
          return;
        }
        if (msg.type === "agent.call_cancelled") {
          setOffer(null);
          pendingAcceptedCallRef.current = "";
          setActiveCallId("");
          setMessage("Call offer cancelled");
          return;
        }
        if (["call.ringing", "call.answered", "call.connected"].includes(msg.type)) {
          const id = String(msg.payload?.canonicalCallId || msg.payload?.callId || pendingAcceptedCallRef.current || "");
          if (id) setActiveCallId(id);
          setState("busy");
          setMessage(msg.type === "call.connected" ? "Call connected" : msg.type === "call.answered" ? "Call answered" : "Connecting call");
          return;
        }
        if (msg.type === "call.completed" || msg.type === "call.failed") {
          setOffer(null);
          pendingAcceptedCallRef.current = "";
          sipSessionRef.current = null;
          if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
          setSipCallActive(false);
          setActiveCallId("");
          setMuted(false);
          setState("connected");
          setMessage(msg.type === "call.completed" ? "Call completed" : "Call failed");
          return;
        }
        if (msg.type === "error") {
          setMessage(String(msg.error || "WebSocket error"));
          setState("error");
        }
      };
      ws.onerror = () => {
        setState("error");
        setMessage("WebSocket connection failed");
      };
      ws.onclose = () => {
        stopHeartbeat();
        if (socketRef.current === ws) socketRef.current = null;
        setState((prev) => (prev === "offline" ? "offline" : "error"));
        setMessage("WebSocket disconnected");
      };
    } catch (err) {
      stopSip();
      setState("error");
      setMessage(err instanceof Error ? err.message : "Could not connect headset");
    } finally {
      setConnecting(false);
    }
  }

  function send(type: string, body: Record<string, unknown> = {}) {
    const ws = socketRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type, ...body }));
  }

  function callId() {
    return offer?.callId || offer?.canonicalCallId || offer?.id || "";
  }

  function acceptOffer() {
    const id = callId();
    if (!id) return;

    if (sipState !== "registered") {
      setMessage("Browser SIP is not registered - cannot accept call");
      return;
    }

    pendingAcceptedCallRef.current = id;
    setActiveCallId(id);
    setMessage("Accepting call");

    send("agent.call.accept", { callId: id });
  }

  function hangupSipCall() {
    const session = sipSessionRef.current;
    if (!session || session.isEnded()) return;

    setMessage("Ending call");
    try {
      session.terminate();
    } catch (err) {
      console.error("Could not hang up SIP call", err);
    }
  }

  function toggleMute() {
    const session = sipSessionRef.current;
    if (!session || session.isEnded()) return;

    try {
      if (muted) {
        session.unmute({ audio: true });
        setMuted(false);
      } else {
        session.mute({ audio: true });
        setMuted(true);
      }
    } catch (err) {
      console.error("Could not change microphone mute state", err);
    }
  }

  useEffect(() => () => closeSocket("offline"), []);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Falcon IVR & Agent Headset</h1>
          <p>Use the browser as an Autodialer agent target for IVR handoff and agent availability.</p>
        </div>
        <div className="row-actions">
          <button className="btn success" onClick={goOnline} disabled={connecting || state === "available" || state === "connected"}>
            Go Online
          </button>
          <button className="btn ghost" onClick={() => closeSocket("offline")}>
            Go Offline
          </button>
        </div>
      </div>

      <div className="headset-grid">
        <section className="card">
          <div className="section-title">
            <h2>Agent</h2>
            <span className={`badge ${state === "available" ? "active" : state === "error" ? "failed" : "paused"}`}>
              {sipCallActive || activeCallId ? "ON CALL" : presence?.agentStatus || state.toUpperCase()}
            </span>
          </div>
          <dl className="detail-list">
            <dt>Desktop Route ID</dt>
            <dd className="mono">{routeId}</dd>
            <dt>Agent identity</dt>
            <dd>{config?.email || presence?.userId || "Not connected"}</dd>
            <dt>SIP endpoint</dt>
            <dd className="mono">{endpoint || "Not configured"}</dd>
            <dt>Service permissions</dt>
            <dd>{contexts.length ? contexts.join(", ") : "None"}</dd>
          </dl>
        </section>

        <section className="card">
          <div className="section-title">
            <h2>Routing</h2>
            <span className={`badge ${presence?.connectionStatus === "ONLINE" ? "active" : "stopped"}`}>
              {presence?.connectionStatus || "OFFLINE"}
            </span>
          </div>
          <p className="muted">{message}</p>
          <div className="row-actions">
            <button className="btn sm" onClick={() => send("agent.status", { status: "AVAILABLE" })} disabled={!token || sipCallActive}>
              Available
            </button>
            <button className="btn sm ghost" onClick={() => send("agent.status", { status: "PAUSED" })} disabled={!token || sipCallActive}>
              Pause
            </button>
          </div>
        </section>
      </div>

      <section className="card">
        <div className="section-title">
          <h2>Browser Headset</h2>
          <span className={`badge ${sipState === "registered" ? "active" : sipState === "error" ? "failed" : "paused"}`}>
            SIP {sipState.toUpperCase()}
          </span>
        </div>

        <audio ref={remoteAudioRef} autoPlay />

        <dl className="detail-list compact">
          <dt>Call</dt>
          <dd className="mono">{activeCallId || "None"}</dd>
          <dt>Audio</dt>
          <dd>{sipCallActive ? (muted ? "Connected - Muted" : "Connected") : "Idle"}</dd>
        </dl>

        {sipCallActive ? (
          <div className="row-actions">
            <button className="btn ghost" onClick={toggleMute}>
              {muted ? "Unmute" : "Mute"}
            </button>
            <button className="btn danger" onClick={hangupSipCall}>
              Hang Up
            </button>
          </div>
        ) : null}
      </section>

      <section className="card headset-offer">
        <div className="section-title">
          <h2>IVR Offer</h2>
          <span className={`badge ${offer ? "ringing" : "draft"}`}>{offer ? "RINGING" : "WAITING"}</span>
        </div>
        {offer ? (
          <>
            <dl className="detail-list compact">
              <dt>Call</dt>
              <dd className="mono">{callId()}</dd>
              <dt>Service</dt>
              <dd>{offer.serviceContext || String(offer.payload?.serviceContext || "") || "Unknown"}</dd>
              <dt>Phone</dt>
              <dd>{offer.phone || String(offer.payload?.phone || "") || "Hidden"}</dd>
            </dl>
            <div className="row-actions">
              <button
                className="btn success"
                onClick={acceptOffer}
                disabled={sipState !== "registered"}
              >
                Accept
              </button>
              <button className="btn ghost" onClick={() => send("agent.call.busy", { callId: callId() })}>
                Busy
              </button>
              <button className="btn danger" onClick={() => send("agent.call.reject", { callId: callId() })}>
                Reject
              </button>
            </div>
          </>
        ) : (
          <p className="muted">Incoming IVR transfers will appear here while the headset is online.</p>
        )}
      </section>
    </div>
  );
}
